import { ExpressionSpecification } from "maplibre-gl";

/**
 * Creates a literal color expression for MapLibre.
 * Used as a fallback when no numeric stops are available.
 *
 * @param color - The color string (e.g. "#CECECE").
 * @returns A MapLibre `literal` expression.
 */
export const toLiteralColorExpression = (
  color: string
): ExpressionSpecification => ["literal", color];

/**
 * Builds a MapLibre `step` color expression from unique color range stops.
 *
 * Separates null (default) entries from numeric stops to avoid the null→0
 * collision bug, and uses `to-number` coercion so string property values
 * are handled safely.
 *
 * @param uniqueRanges - Output of `generateColorRangesUniqueStops`.
 *                       First element of each tuple is `null` (default) or a numeric stop.
 * @param category     - The feature property name to read (e.g. "Score").
 * @returns A valid `ExpressionSpecification` — either a `step` expression or a `literal` fallback.
 */
export const buildStepColorExpression = (
  uniqueRanges: [number | null, string][],
  category: string
): ExpressionSpecification => {
  // Separate null (default) entry from numeric stops
  const defaultEntry = uniqueRanges.find(([v]) => v === null);
  const defaultColor = defaultEntry ? defaultEntry[1] : "#CECECE";
  const numericStops = uniqueRanges.filter(
    (entry): entry is [number, string] => entry[0] !== null
  );

  if (numericStops.length >= 1) {
    const flatStops = numericStops.flatMap(([v, c]) => [v, c]);

    return [
      "step",
      ["to-number", ["get", category], -1],
      defaultColor,
      ...flatStops
    ];
  }

  // No numeric stops — return constant color
  return toLiteralColorExpression(defaultColor);
};
